import { Directive, ElementRef, OnDestroy, effect, input, output } from '@angular/core';
import { IFileMetadata, ImageUtilitiesService } from '@utilities/image-utilities.service';
import { Subscription } from 'rxjs';

// A transient failure (a flaky network blip, a brief backend hiccup) used to leave a tile stuck
// on the placeholder for the rest of the session, since nothing ever retried it. A couple of
// quick retries before giving up costs little and self-heals the common transient case;
// MAX_LOAD_ATTEMPTS caps it so a genuinely missing/broken image doesn't retry forever.
const MAX_LOAD_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

@Directive({
  selector: '[appImageLoader]',
})
export class ImageLoaderDirective implements OnDestroy {
  public readonly appImageLoader = input.required<string>();

  public readonly imageLoading = output<void>();
  public readonly imageLoaded = output<number>();
  public readonly imageError = output<void>();

  private readonly placeholderImage: string = new URL('icons/image-placeholder.svg', document.baseURI).toString();

  private subscription?: Subscription;
  // The exact cacheKey getImageUrlAndSize() returned for our current reference (see
  // ImageUtilitiesService.getImageUrlAndSize/releaseImage) - captured once at acquire time and
  // held as-is, rather than re-derived from an id later. getApiUrl (which computes this key)
  // depends on the CURRENT route, and cleanup() commonly runs as part of navigating away, after
  // the route has already changed - re-deriving at that point could resolve to a different key
  // than the one actually acquired. Also why this is tracked separately from appImageLoader():
  // cleanup() must release whichever reference we PREVIOUSLY took, not whatever the input signal
  // has already moved on to by the time cleanup runs.
  private cachedKey?: string;
  private retryTimeoutId?: ReturnType<typeof setTimeout>;
  private attempt = 0;

  constructor(
    private readonly el: ElementRef<HTMLImageElement>,
    private readonly imageService: ImageUtilitiesService,
  ) {
    effect(() => {
      const id = this.appImageLoader();

      this.cleanup();

      if (!id || id.includes('icons/')) {
        return;
      }

      this.attempt = 0;
      this.loadImage(id);
    });
  }

  public ngOnDestroy(): void {
    this.cleanup();
  }

  private loadImage(id: string): void {
    this.el.nativeElement.src = ImageUtilitiesService.transparentImage;

    this.imageLoading.emit();

    const { cacheKey, observable } = this.imageService.getImageUrlAndSize(id);

    this.cachedKey = cacheKey;
    this.subscription = observable.subscribe({
      next: ({ url, size }: IFileMetadata) => {
        this.el.nativeElement.src = url;
        this.imageLoaded.emit(size);
      },

      error: () => {
        this.retryOrFallback(id);
      },
    });
  }

  private retryOrFallback(id: string): void {
    // Each getImageUrlAndSize() call takes its own cache reference; this one failed, so release
    // it before (maybe) taking a new one for the retry, or it would be left double-counted.
    this.releaseCachedImage();
    this.attempt++;

    if (this.attempt >= MAX_LOAD_ATTEMPTS) {
      this.el.nativeElement.src = this.placeholderImage;
      this.imageError.emit();

      return;
    }

    this.retryTimeoutId = setTimeout(() => {
      this.loadImage(id);
    }, RETRY_DELAY_MS);
  }

  private releaseCachedImage(): void {
    if (this.cachedKey) {
      this.imageService.releaseImage(this.cachedKey);
      this.cachedKey = undefined;
    }
  }

  private cleanup(): void {
    if (this.retryTimeoutId !== undefined) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = undefined;
    }

    this.releaseCachedImage();

    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
